import React, { Fragment, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  CircularProgress,
} from '@mui/material';
import { css } from 'aphrodite';
import CloseIcon from '@mui/icons-material/Close';
import styles from './styles';
import FolderPickerContextBanner from './FolderPickerContextBanner';
import FolderPickerTable from './FolderPickerTable';
import { FolderData } from './types';
import { useClioMatterFolder } from '~/src/hooks/useClioDocumentPicker';
import { OpenInNewOutlined } from '@mui/icons-material';

export type FolderPickerModalProps = {
  shouldOpen: boolean;
  onClose: () => void;
  onConfirm: (folderId: string | number | null) => void;
  matterId: string;
};

type FolderClickHandler = (rowData: FolderData) => void;

const TITLE = 'Save to Clio Manage';
const BUTTON_TEXT = 'Continue to Clio Manage';

const FolderPickerModal = ({
  shouldOpen,
  onClose,
  onConfirm,
  matterId,
}: FolderPickerModalProps) => {
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const rootmatterFolderQuery = useClioMatterFolder(matterId, shouldOpen);

  const handleClose = () => {
    setSelectedId(null);
    onClose();
  };

  const handleConfirm = () => {
    onConfirm(selectedId);
    onClose();
  };

  const folderData = rootmatterFolderQuery.data?.folder;

  // Auto-select the matter's root folder as default when data becomes available
  useEffect(() => {
    if (folderData && shouldOpen) {
      setSelectedId((currentId) =>
        currentId === null ? folderData.id : currentId,
      );
    }
  }, [folderData, shouldOpen]);

  // Early return after all hooks if modal should not be open
  if (!shouldOpen) {
    return null;
  }

  // Validate folder data before creating matterRootFolder
  const matterRootFolder: FolderData | null = folderData
    ? {
        id: folderData.id,
        name: folderData.name,
        level: 0,
        parentId: null,
        expanded: false,
        isSelected: true,
      }
    : null;

  const currentMatterName = rootmatterFolderQuery.data?.display_number;

  const isLoading = rootmatterFolderQuery.isLoading;
  const error = rootmatterFolderQuery.error;

  const isDisabled = selectedId === null || isLoading || !matterRootFolder;

  const handleRowClick: FolderClickHandler = (rowData) => {
    setSelectedId(rowData.id);
  };

  return (
    <Dialog
      open={shouldOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        className: css(styles.dialogPaper),
      }}
    >
      <DialogTitle className={css(styles.dialogTitle)}>
        <Box className={css(styles.dialogTitleText)}>{TITLE}</Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          className={css(styles.closeButton)}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className={css(styles.dialogContent)}>
        <FolderPickerContextBanner
          currentMatterName={currentMatterName || 'Current Matter'}
        />

        {isLoading && (
          <Box className="flex justify-center items-center py-8">
            <CircularProgress size={24} />
          </Box>
        )}
        {error && (
          <Box className="flex justify-center items-center py-8 text-red-500">
            {error.message}
          </Box>
        )}
        {!isLoading && !error && (
          <Fragment>
            {matterRootFolder && (
              <FolderPickerTable
                rootFolder={matterRootFolder}
                selectedId={selectedId}
                onRowClick={handleRowClick}
              />
            )}
            {!matterRootFolder && (
              <Box className="flex justify-center items-center py-8 text-gray-500">
                No folder data available
              </Box>
            )}
          </Fragment>
        )}
      </DialogContent>

      <DialogActions className={css(styles.dialogActions)}>
        <Button
          variant="outlined"
          onClick={handleClose}
          className={css(styles.cancelButton)}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isDisabled}
          onClick={handleConfirm}
          className={css(
            styles.confirmButton,
            isDisabled && styles.confirmButtonDisabled,
          )}
        >
          {BUTTON_TEXT}
          <OpenInNewOutlined className={css(styles.confirmButtonIcon)} />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FolderPickerModal;
